<template>
  <section v-if="page" class="wiki-article">
    <div class="article-header">
      <div>
        <p class="breadcrumb">
          <RouterLink to="/wiki">ويكي المؤسسة</RouterLink>
          <span>/</span>
          <span>{{ page.title }}</span>
        </p>
        <h1>{{ page.title }}</h1>
        <div class="meta">
          <span>📅 {{ formatDate(page.event_date) }}</span>
          <span>آخر تحديث {{ formatDateTime(page.updated_at) }}</span>
        </div>
      </div>
      <div class="actions">
        <WikiSearchBar @select="handleSuggestion" />
        <div class="admin-actions" v-if="auth.isAdminRole">
          <Button
            label="تعديل"
            icon="pi pi-pencil"
            severity="secondary"
            @click="editPage"
          />
          <Button
            label="حذف"
            icon="pi pi-trash"
            severity="danger"
            outlined
            :loading="deletingPage"
            @click="confirmDeletePage"
          />
        </div>
      </div>
    </div>

    <div class="tags" v-if="page.tags.length">
      <span>وسوم:</span>
      <div
        v-for="tag in page.tags"
        :key="tag.tag_id ?? `${tag.entity_type || 'tag'}-${tag.entity_id ?? tag.slug ?? tag.label}`"
        class="tag-chip"
      >
        <RouterLink
          v-if="tag.slug"
          class="tag-chip-link"
          :to="`/tags/${tag.slug}`"
        >
          #{{ tag.label }}
          <small v-if="tag.entity_type && tag.entity_type !== 'tag'">({{ formatTagType(tag.entity_type) }})</small>
        </RouterLink>
        <span v-else class="tag-chip-link is-static">
          #{{ tag.label }}
          <small v-if="tag.entity_type && tag.entity_type !== 'tag'">({{ formatTagType(tag.entity_type) }})</small>
        </span>
      </div>
    </div>

    <article class="rich-text" v-html="page.content_html"></article>

    <section class="linked" v-if="page.linked_employees.length">
      <h3>الموظفون المرتبطون</h3>
      <ul>
        <li v-for="emp in page.linked_employees" :key="emp.employee_id">
          <RouterLink :to="`/employee/${emp.employee_id}`">
            {{ [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.employee_id }}
          </RouterLink>
        </li>
      </ul>
    </section>

    <section v-if="page.attachments?.length" class="attachments">
      <h3>المرفقات</h3>
      <ul>
        <li v-for="attachment in page.attachments" :key="attachment.attachment_id">
          <div class="attachment-header">
            <div>
              <a
                class="text-primary hover:underline font-semibold"
                target="_blank"
                rel="noopener"
                :href="attachmentDownloadUrl(attachment.attachment_id)"
              >
                {{ attachment.title || attachment.summary || attachment.url }}
              </a>
              <p v-if="attachment.summary" class="attachment-summary">
                {{ attachment.summary }}
              </p>
              <a
                v-if="attachment.url"
                class="text-primary text-xs block truncate mt-1"
                target="_blank"
                rel="noopener"
                :href="attachment.url"
              >
                {{ attachment.url }}
              </a>
            </div>
            <div class="attachment-actions">
              <button
                v-if="auth.isAdminRole"
                type="button"
                class="attachment-refresh-button"
                :disabled="refreshingAttachmentId === attachment.attachment_id"
                @click="refreshAttachment(attachment.attachment_id)"
              >
                <i class="pi pi-refresh" />
                <span>تحديث</span>
              </button>
              <button
                v-if="auth.isAdminRole"
                type="button"
                class="attachment-delete-button"
                :disabled="deletingLinkAttachmentId === attachment.attachment_id"
                @click="removeLinkAttachment(attachment.attachment_id)"
                aria-label="حذف المرفق"
              >
                ×
              </button>
            </div>
          </div>
        </li>
      </ul>
    </section>

    <section v-if="page.wiki_attachments?.length" class="wiki-attachments">
      <h3>مرفقات الويكي</h3>
      <ul>
        <li v-for="attachment in page.wiki_attachments" :key="attachment.attachment_id">
          <div class="wiki-attachment-row">
            <div>
              <a
                class="text-primary hover:underline font-semibold"
                :href="attachment.url"
                target="_blank"
                rel="noopener"
              >
                {{ attachment.file_name }}
              </a>
              <p class="attachment-summary">
                {{ attachment.display_type }}
              </p>
            </div>
          </div>
        </li>
      </ul>
    </section>
  </section>
  <p v-else class="text-muted text-center py-6">...جار التحميل</p>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import WikiSearchBar from '@/components/wiki/WikiSearchBar.vue'
import {
  deleteWikiPage,
  fetchWikiPage,
  type WikiPageDetail,
  type WikiTagLink,
} from '@/services/wiki'
import { refreshLinkAttachment, deleteLinkAttachment as deleteLinkAttachmentService } from '@/services/linkAttachments'
import { useAuthStore } from '@/store/auth'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const toast = useToast()
const page = ref<WikiPageDetail | null>(null)
const deletingPage = ref(false)
const refreshingAttachmentId = ref<number | null>(null)
const deletingLinkAttachmentId = ref<number | null>(null)

async function loadPage() {
  page.value = null
  try {
    page.value = await fetchWikiPage(route.params.slug as string)
  } catch (err) {
    console.error('Failed to load wiki page', err)
  }
}

onMounted(loadPage)
watch(() => route.params.slug, loadPage)

function formatDate(value?: string | null) {
  if (!value) return 'غير محدد'
  return new Date(value).toLocaleDateString('ar-EG')
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleDateString('ar-EG')
}

const attachmentDownloadUrl = (id: number) => `/api/link-attachments/${id}/download`

function editPage() {
  router.push(`/wiki/${route.params.slug}/edit`)
}

async function refreshAttachment(attachmentId: number) {
  if (!auth.isAdminRole) return
  refreshingAttachmentId.value = attachmentId
  try {
    await refreshLinkAttachment(attachmentId)
    await loadPage()
  } catch (err) {
    console.error('Failed to refresh attachment', err)
  } finally {
    refreshingAttachmentId.value = null
  }
}

async function removeLinkAttachment(attachmentId: number) {
  if (!auth.isAdminRole) return
  deletingLinkAttachmentId.value = attachmentId
  try {
    await deleteLinkAttachmentService(attachmentId)
    toast.add({ severity: 'success', summary: 'تم حذف المرفق', life: 2500 })
    await loadPage()
  } catch (err) {
    console.error('Failed to delete attachment', err)
    toast.add({ severity: 'error', summary: 'تعذر حذف المرفق', life: 3000 })
  } finally {
    deletingLinkAttachmentId.value = null
  }
}

function formatTagType(type?: string | null) {
  if (!type) return ''
  const map: Record<string, string> = {
    company: 'شركة',
    department: 'قسم',
    rank: 'رتبة',
    office: 'مكتب',
    title: 'منصب',
    hierarchy: 'هيكل',
    city: 'مدينة',
    state: 'ولاية',
    country: 'دولة',
  }
  return map[type] || type
}

function handleSuggestion(option: { type: string; value: any }) {
  if (!option) return
  if (option.type === 'article') router.push(`/wiki/${option.value.slug}`)
  if (option.type === 'tag') router.push({ path: '/wiki', query: { tag: option.value.slug } })
  if (option.type === 'employee')
    router.push({ path: '/wiki', query: { employeeId: option.value.employee_id } })
}

async function confirmDeletePage() {
  if (!page.value) return
  if (!window.confirm('هل أنت متأكد من حذف هذه المقالة بشكل نهائي؟')) return
  deletingPage.value = true
  try {
    await deleteWikiPage(page.value.slug)
    toast.add({ severity: 'success', summary: 'تم حذف المقال', life: 2000 })
    router.push('/wiki')
  } catch (err: any) {
    toast.add({
      severity: 'error',
      summary: 'تعذر حذف المقال',
      detail: err?.response?.data?.detail || 'حدث خلل أثناء الحذف',
      life: 4000,
    })
  } finally {
    deletingPage.value = false
  }
}
</script>

<style scoped>
.wiki-article {
  max-width: 860px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.article-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.breadcrumb {
  font-size: 0.9rem;
  color: #475569;
  display: flex;
  gap: 0.5rem;
}

.breadcrumb a {
  color: #2563eb;
  text-decoration: none;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  color: #475569;
  font-size: 0.9rem;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tags {
  margin: 1rem 0;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.tags span {
  font-weight: 600;
}

.tag-chip {
  display: inline-flex;
}

.tag-chip-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.85rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #0f172a;
  text-decoration: none;
  border: 1px solid transparent;
  transition: background 0.2s ease, color 0.2s ease;
}

.tag-chip-link:hover {
  background: #e2e8f0;
}

.tag-chip-link.is-static {
  cursor: default;
  opacity: 0.75;
}

.rich-text {
  line-height: 1.8;
  color: #0f172a;
}

.rich-text :global(img) {
  width: auto;
  height: auto;
  max-width: none;
  border-radius: 0.5rem;
  margin: 1rem 0;
  resize: both;
  overflow: auto;
  cursor: se-resize;
  display: inline-block;
  min-width: 50px;
  min-height: 50px;
}

.linked {
  margin-top: 2rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;
}

.linked ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.linked a {
  color: #2563eb;
  text-decoration: none;
}

.attachments {
  margin-top: 2rem;
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.attachments ul {
  list-style: disc;
  margin: 0;
  padding-left: 1.5rem;
}

.attachments li {
  margin-bottom: 0.5rem;
}

.attachment-summary {
  font-size: 0.85rem;
  color: #475569;
  margin: 0.25rem 0 0;
}

.attachment-refresh-button {
  margin-top: 0.35rem;
  padding: 0.25rem 0.65rem;
  font-size: 0.75rem;
  border-radius: 999px;
  border: 1px solid #cbd5f5;
  background: #eef2ff;
  color: #2563eb;
  display: inline-flex;
  gap: 0.25rem;
  align-items: center;
}

.attachment-refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.attachment-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.attachment-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.attachment-delete-button {
  border: 1px solid #f87171;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 999px;
  font-size: 1rem;
  width: 1.5rem;
  height: 1.5rem;
  line-height: 1;
  cursor: pointer;
}

.attachment-delete-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wiki-attachments {
  margin-top: 1.5rem;
}

.wiki-attachments ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.wiki-attachment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem;
  background: #f8fafc;
}

.attachment-summary {
  font-size: 0.75rem;
  color: #475569;
  margin: 0.25rem 0 0;
}
</style>
